import { RELEASE_KINDS, UPDATES_IN_DIALOG, UPDATES_MSG, type HelpIndex, type UpdateItem, type UpdatesResponse } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api } from "../api.js";
import { PASSIVE } from "../lib/autoLogout.js";
import { currentProduct } from "../lib/product.js";
import { record } from "../lib/trail.js";
import { useHelpIndex } from "./HelpPanel.jsx";
import { BellIcon } from "./icons.jsx";
import { Sheet } from "./Sheet.jsx";

/**
 * 更新情報（#204、docs/83 §5・★4）。運営が管理アプリで公開した更新だけ（GET /updates。言葉は製品の既定で埋まって届く）。
 *   - 左の列・☰ の「お知らせ」（#206、docs/83 §14：前は「ヘルプ」の窓のいちばん上の段＝ヘルプとサポートと混ざって見づらかった）
 *     → 窓（UpdatesDialog）：公開済みの更新の一覧（新しい順・区分チップ・新着・題と1行目）→ 押すと窓の中で本文 →「ヘルプで見る」（公開の記事のとき）。
 *       いちばん下に「更新情報の一覧」（/updates）。公開が0件なら「新しいお知らせはありません」の1行。帯にしない（docs/67：常に出ている帯を置かない）
 *   - 未読があれば「お知らせ」に朱の点（「ヘルプ」には付けない）。窓を開いた時点で見た印（POST /updates/seen）＝点が消える
 *   - /updates（ログイン前でも読める。pages/Updates.tsx）
 * どれも人の操作に数えない（PASSIVE）。メール・プッシュは送らない
 */

export const updatesKey = () => ["updates", currentProduct()] as const;

/** 公開済みの更新と未読の数。届かない・形が違うときは undefined のまま（点も出さない） */
export function useUpdates(enabled = true) {
  return useQuery({
    queryKey: updatesKey(),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () => api<UpdatesResponse>("GET", "/updates", undefined, PASSIVE),
  });
}

/** 誘導する記事が公開のヘルプにあるか（下書きのうちは「ヘルプで見る」を出さない） */
export const publishedHelp = (index: HelpIndex | null | undefined, id: string | undefined): boolean =>
  !!id && !!index?.articles.some((a) => a.id === id && a.status === "published");

/**
 * 窓を開いたら見た印を付ける（未読があるときだけ1回）。返すのは開いたときに未読だった更新の id
 * （見た印を付けたあとも、この窓の中では「新着」を残す）。届く前は null
 */
export function useMarkSeen(data: UpdatesResponse | undefined): ReadonlySet<string> | null {
  const qc = useQueryClient();
  const [newAtOpen, setNewAtOpen] = useState<ReadonlySet<string> | null>(null);
  const done = useRef(false);
  useEffect(() => {
    if (!data || done.current) return;
    done.current = true;
    setNewAtOpen(new Set(data.items.filter((i) => i.isNew).map((i) => i.id)));
    if (data.unread <= 0) return;
    void api<{ ok: true; seenAt: string }>("POST", "/updates/seen", {}, PASSIVE)
      .then((r) => qc.setQueryData<UpdatesResponse>(updatesKey(), (x) => (x ? { ...x, unread: 0, seenAt: r.seenAt, items: x.items.map((i) => ({ ...i, isNew: false })) } : x)))
      .catch(() => undefined);
  }, [data, qc]);
  return newAtOpen;
}

const firstLine = (s: string): string => (s.split(/(?<=[。！？])|\n/)[0] ?? s).trim();

/** 区分のしるし（色だけで分けない＝文字） */
export function KindChip({ kind }: { kind: UpdateItem["kind"] }) {
  return <span className={`chip upkind is-${kind}`}>{RELEASE_KINDS[kind]}</span>;
}

/**
 * 「お知らせ」の項目（#206）：左の列（44px・縮小のときはアイコンだけ）と ☰ のメニュー（56px）。「ヘルプ」の上。見た目は「ヘルプ」と同じ（.navhelp）。
 * 未読があれば朱の点（工程表のメニューの朱の丸と同じ .nbadge の点の形。数は読み上げだけ）
 */
export function UpdatesNavButton({ onOpen, unread = 0 }: { onOpen: (el: HTMLButtonElement) => void; unread?: number }) {
  const label = UPDATES_MSG.navLabel(unread);
  return (
    <button type="button" className="navhelp navupd" aria-label={label} title={label} aria-haspopup="dialog" onClick={(e) => onOpen(e.currentTarget)} data-testid="updates-open">
      <BellIcon />
      <span data-t={UPDATES_MSG.nav}>{UPDATES_MSG.nav}</span>
      {unread > 0 ? <i className="nbadge is-dot" aria-hidden="true" data-testid="nav-updates-dot" /> : null}
    </button>
  );
}

/** 窓の中で開いた1件 */
function UpdateArticle({ u, helpIndex, onBack, onHelp }: { u: UpdateItem; helpIndex: HelpIndex | null | undefined; onBack: () => void; onHelp: (id: string) => void }) {
  const title = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    title.current?.focus();
  }, [u.id]);
  return (
    <article className="upart" data-testid="updates-article" data-update={u.id}>
      <button type="button" className="btn text upback" onClick={onBack} data-testid="updates-back">
        ‹ {UPDATES_MSG.back}
      </button>
      <h3 ref={title} tabIndex={-1}>
        {u.title}
      </h3>
      <p className="upmeta">
        <KindChip kind={u.kind} />
        <span>{UPDATES_MSG.date(u.publishedAt)}</span>
      </p>
      <p className="upbody">{u.body}</p>
      {u.help && publishedHelp(helpIndex, u.help.id) ? (
        <p>
          <button
            type="button"
            className="btn"
            onClick={() => {
              record("help", `お知らせから記事へ：${u.help!.id}`);
              onHelp(u.help!.id);
            }}
            data-testid="updates-help"
          >
            {UPDATES_MSG.openHelp}
          </button>
        </p>
      ) : null}
    </article>
  );
}

/**
 * 「お知らせ」の窓（#206）。PC は真ん中の枠、スマホ（スマホ版と幅 767px 以下）は下からのシート（Sheet の閉じるだけの形）。
 * 画面の写しに入れない（data-nocapture）。onHelp＝「ヘルプで見る」（この窓を閉じて「ヘルプ」の窓でその記事を開く＝Shell）
 */
export function UpdatesDialog({ open, onClose, onHelp }: { open: boolean; onClose: () => void; onHelp: (id: string) => void }) {
  const q = useUpdates();
  const help = useHelpIndex();
  const newAtOpen = useMarkSeen(open ? q.data : undefined);
  const [openId, setOpenId] = useState<string | null>(null);
  /** 本文から一覧へ戻ったら、開いた1件のボタンへ */
  const backTo = useRef<string | null>(null);
  const list = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (openId || !backTo.current) return;
    list.current?.querySelector<HTMLElement>(`[data-update="${backTo.current}"]`)?.focus();
    backTo.current = null;
  }, [openId]);
  const items = (q.data?.items ?? []).slice(0, UPDATES_IN_DIALOG);
  const cur = openId ? items.find((u) => u.id === openId) ?? null : null;
  return (
    <Sheet open={open} title={UPDATES_MSG.nav} closeLabel="閉じる" onCancel={onClose} testId="updates-dialog" closeTestId="updates-close" className="updsheet" noCapture>
      {q.isPending ? (
        <p className="hint upnone" data-testid="updates-loading">
          読み込んでいます…
        </p>
      ) : q.isError ? (
        <p className="upnone" role="alert">
          {UPDATES_MSG.loadFailed}
        </p>
      ) : !items.length ? (
        <p className="upnone" data-testid="updates-none">
          {UPDATES_MSG.none}
        </p>
      ) : cur ? (
        <UpdateArticle
          u={cur}
          helpIndex={help.data}
          onBack={() => {
            backTo.current = cur.id;
            setOpenId(null);
          }}
          onHelp={onHelp}
        />
      ) : (
        <>
          <ul className="uplist" ref={list} data-testid="updates-list">
            {items.map((u) => {
              const isNew = !!newAtOpen?.has(u.id);
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    className={"upitem" + (isNew ? " is-new" : "")}
                    onClick={() => {
                      record("help", `お知らせを開いた：${u.title}`);
                      setOpenId(u.id);
                    }}
                    data-update={u.id}
                  >
                    <span className="upitem-h">
                      <KindChip kind={u.kind} />
                      {isNew ? <span className="chip ai upnewmark">{UPDATES_MSG.newMark}</span> : null}
                      <span className="upitem-d">{UPDATES_MSG.date(u.publishedAt)}</span>
                    </span>
                    <span className="upitem-t">{u.title}</span>
                    <span className="upitem-b">{firstLine(u.body)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="upall">
            <a href="/updates" target="_blank" rel="noopener" data-testid="updates-all">
              {UPDATES_MSG.all}
            </a>
          </p>
        </>
      )}
    </Sheet>
  );
}
